/**
 * Data-flag API helpers — "Flag bad data" feature.
 *
 * Lives in a separate module from services/api.js so the flag
 * surface stays self-contained (the parent feature is small and
 * may grow its own admin-review UI later — keeping its API client
 * isolated avoids the ParcelDetail import bloat we already have
 * from the bookmark + downvote + comparable-parcels helpers piling
 * into services/api.js).
 *
 * Uses fetch() directly rather than the shared axios client in
 * services/api.js. The shared client adds auth interceptors and a
 * 401-redirect side effect; data flags are explicitly allowed for
 * anonymous users (see the backend migration's reporter_email
 * nullable column) so we don't want the auth interceptor kicking in.
 * Errors are surfaced with the backend's `detail` payload when
 * present so the caller can render them inline.
 */

const API_BASE = process.env.REACT_APP_API_URL || '/api/v1';

/**
 * Build a fetch() error that carries the backend's JSON detail.
 *
 * FastAPI returns either `{ detail: "..." }` (HTTPException) or
 * `{ detail: [{...}] }` (Pydantic 422). We surface the human string
 * when we can and fall back to the HTTP status otherwise so the
 * frontend toast never shows "[object Object]".
 */
async function _toError(response) {
  let detail = null;
  try {
    const body = await response.json();
    if (typeof body?.detail === 'string') {
      detail = body.detail;
    } else if (Array.isArray(body?.detail) && body.detail[0]?.msg) {
      detail = body.detail[0].msg;
    }
  } catch {
    // Non-JSON body — keep detail null.
  }
  const err = new Error(detail || `Request failed: ${response.status}`);
  err.status = response.status;
  err.detail = detail;
  return err;
}

/**
 * Submit a data-quality flag for a parcel.
 *
 * @param {string} parcelId - the parcel_id (county or RC- shim form).
 * @param {object} payload - { category, comment?, reporter_email? }.
 *   `category` must be one of the values the backend Pydantic
 *   Literal accepts (see api/v1/data_flags.py); the dropdown in the
 *   modal is the source of truth client-side.
 * @returns the freshly-inserted flag row (id, created_at, ...).
 */
export async function submitDataFlag(parcelId, { category, comment, reporter_email } = {}) {
  const body = { category };
  // Only attach optional fields when present so we don't send
  // empty strings the backend would have to special-case.
  if (comment && comment.trim()) body.comment = comment.trim();
  if (reporter_email && reporter_email.trim()) body.reporter_email = reporter_email.trim();

  const response = await fetch(
    `${API_BASE}/parcels/${encodeURIComponent(parcelId)}/flags`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  if (!response.ok) {
    throw await _toError(response);
  }
  return response.json();
}

/**
 * List all flags previously filed against a parcel.
 *
 * Used by the parcel detail page to surface the "N flags reported"
 * indicator next to the Flag-bad-data button. Returns
 * { parcel_id, count, unresolved_count, flags: [...] }.
 */
export async function listDataFlagsForParcel(parcelId) {
  const response = await fetch(
    `${API_BASE}/parcels/${encodeURIComponent(parcelId)}/flags`,
    { method: 'GET' },
  );
  if (!response.ok) {
    throw await _toError(response);
  }
  return response.json();
}
